import {motion, MotionStyle} from 'framer-motion';
import {
  getColorByPath,
  getSize,
  Integer,
  ThemeColorPath,
  ThemeRadiusPath,
} from 'platform/foundation';
import styled, {css, keyframes} from 'styled-components';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda-adjunct';

export interface SkeletonBoxProps {
  width?: Integer;
  height?: Integer;
  borderRadius?: ThemeRadiusPath;
  backgroundColor?: ThemeColorPath;
  isLoading?: boolean;
  children?: ReactNode;
  contentStyles?: MotionStyle;
}

const pulse = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 0.6;
  }
`;

const StyledSkeletonBox = styled.div<Omit<SkeletonBoxProps, 'children'>>`
  ${(props) => css`
    width: ${isNotNil(props.width) ? getSize(props.width) : '100%'};
    height: ${isNotNil(props.height) ? getSize(props.height) : '100%'};
    border-radius: ${({theme}) =>
      isNotNil(props.borderRadius) ? theme.radii[props.borderRadius] : '4px'};
    background-color: ${({theme}) =>
      getColorByPath(props.backgroundColor, theme) ?? theme.colors.palettes.neutral[50][100]};
    animation: ${pulse} 1.5s ease-in-out infinite;
  `}
`;

const FADE_IN_DURATION = 0.2;

export function SkeletonLoadingBox(props: SkeletonBoxProps) {
  if (props.isLoading) {
    return (
      <motion.div
        key="skeleton"
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        transition={{duration: FADE_IN_DURATION}}
      >
        <StyledSkeletonBox
          width={props.width}
          height={props.height}
          borderRadius={props.borderRadius}
          backgroundColor={props.backgroundColor}
        />
      </motion.div>
    );
  }

  // Content must not wait for an exit animation: refetches can finish during the transition.
  return (
    <motion.div
      key="content"
      style={{
        height: 'inherit',
        width: 'inherit',
        ...props.contentStyles,
      }}
    >
      {props.children}
    </motion.div>
  );
}
